'use client';

import React, { useCallback, useEffect, useId, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { CrossDark } from 'assets/icons';
import CustomLink from 'components/shared/CustomLink';
import {
  ConsentPlugType,
  PRIVACY_POLICY_URL,
  TERMS_OF_USE_URL,
} from 'content/powerPlugRedeem';
import styles from './ConsentModal.module.css';

interface ConsentModalProps {
  plugType: ConsentPlugType;
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const ConsentModal = ({
  className,
  plugType,
  isOpen,
  isLoading,
  onClose,
  onConfirm,
}: ConsentModalProps & { className?: string }) => {
    const t = useTranslations('powerPlugsRedeem');
    const titleId = useId();
    const dialogRef = useRef<HTMLDivElement>(null);
    const checkboxRef = useRef<HTMLInputElement>(null);
    const [agreed, setAgreed] = React.useState(false);

    const disclaimers = t.raw(
      `consent.${plugType}.disclaimers`,
    ) as unknown as string[];

    const intro = t(`consent.${plugType}.intro`);

    useEffect(() => {
      if (!isOpen) {
        setAgreed(false);
        return;
      }

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      dialogRef.current?.focus();

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen, onClose]);

    const handleBackdropClick = useCallback(
      (event: React.MouseEvent) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      },
      [onClose],
    );

    if (!isOpen) return null;

    return (
      <div
        className={className ? `${styles.root} ${className}` : styles.root}
        onClick={handleBackdropClick}
        aria-hidden={false}
      >
        <div
          ref={dialogRef}
          className={styles.modalContent}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
        >
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t('consent.close')}
          >
            <CrossDark />
          </button>

          <h2 id={titleId}>{t(`consent.${plugType}.title`)}</h2>

          {intro ? <p className={styles.intro}>{intro}</p> : null}

          <ol className={styles.disclaimers}>
            {disclaimers.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ol>

          <label className={styles.checkboxLabel}>
            <input
              ref={checkboxRef}
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>
              {t('consent.checkboxBeforeTerms')}{' '}
              <CustomLink href={TERMS_OF_USE_URL} target="_blank">
                {t('consent.termsLink')}
              </CustomLink>{' '}
              {t('consent.checkboxBetween')}{' '}
              <CustomLink href={PRIVACY_POLICY_URL} target="_blank">
                {t('consent.privacyLink')}
              </CustomLink>
              {t('consent.checkboxAfter')}
            </span>
          </label>

          <button
            type="button"
            className={styles.submit}
            disabled={!agreed || isLoading}
            onClick={onConfirm}
          >
            {isLoading ? t('form.submitting') : t('consent.submit')}
          </button>
        </div>
      </div>
    );
};
